<template>
  <div>
    <Breadcrumbs />
    <div class="wrapper mt-5 mb-5 pb-5">
      <div class="container">
        <h1 class="mb-4 text-black">{{
          $t("shopping_basket")
        }}</h1>
        <form class="cart-details my-5">
          <div class="cart-heading border-bottom border-black d-flex flex-column flex-md-row justify-content-between align-items-center py-4">
            <div class="cart-heading-items d-flex align-items-center mb-5 mb-md-2">
              <ClientOnly><img src="@/esf_antarctica_lederhosenland/assets/imgs/cartpage-icon.svg" class="cart-icon" alt=""></ClientOnly>
              <h4 class="mb-0 ms-2 ms-md-4">{{ $t("you_have") }} <span class="text-primary">{{ cartCount }}</span> {{ $t("item_in_your_shopping_cart") }} </h4>
            </div>
            <div class="d-flex justify-content-end align-items-center mb-md-2">
              <b-link @click="$router.push('/')" href="#" style="text-decoration: underline;">{{ $t("continue_shopping") }}</b-link>
              <button class="btn btn-primary text-uppercase ms-4 px-5" type="button" @click="$router.push('/checkout')">{{ $t("continue_buying") }}</button>
            </div>
          </div>
          <div class="cart-items" v-if="cartItems.length > 0">
            <div class="cart-item border-bottom border-black d-flex py-5" v-for="cartItem of cartItems" :key="cartItem.id">
              <b-link :to="`/${cartItem.product.url_key}`" class="cart-item-image align-self-start border border-secondary">
                <img class="img-fluid object-fit-contain" :src="cartItem.product.image.url" :alt="cartItem.product.name" width="215" height="180">
              </b-link>
              <div class="cart-item-details ms-3 ms-md-5 w-100 me-lg-3 pe-lg-5">
                <h5 class="cart-item-name mb-4">
                  <b-link :to="`/${cartItem.product.url_key}`">
                    {{
                      cartItem.product.name
                    }}
                  </b-link>
                </h5>
                <div class="cart-item-meta d-flex flex-column flex-md-row justify-content-between align-items-stretch w-100">
                  <div class="cart-item-meta-size d-flex flex-column justify-content-between">
                    <div v-if="typeof cartItem.configurable_options != 'undefined'">
                      <div
                        class="cart-item-size text-uppercase"
                        v-for="(option, index) of cartItem.configurable_options"
                        :key="index"
                      >{{ option.option_label }}: {{ option.value_label }}
                      </div>
                    </div>
                    <div v-if="cartItem.bundle_options !== undefined">
                      <ul>
                        <li
                          v-for="option of cartItem.bundle_options"
                          :key="option.id"
                        >
                          <span>{{ option.label }}</span>
                          <div v-if="option.values">
                            <div class="sub" v-for="val of option.values" :key="val.label">
                              <span>
                                - {{ val.quantity }}X{{
                                  val.label
                                }}
                              </span>
                              <span>
                                {{ formatCurrency(val.price) }}
                              </span>
                            </div>
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div class="cart-item-meta-qty-price d-flex flex-column flex-lg-row justify-content-between align-items-start">
                    <div class="cart-item-qty order-2 order-lg-1">
                      <div class="input-group">
                        <button class="btn" type="button" @click="quantityDown(cartItem.id, cartItem.quantity)">-</button>
                        <input type="text" min="1" class="form-control" :value="cartItem.quantity" name="quantity" ref="inputQuantity" disabled>
                        <button class="btn" type="button" @click="quantityUp(cartItem.id, cartItem.quantity)">+</button>
                      </div>
                    </div>
                    <div class="cart-item-price fs-2 fw-bold text-primary order-1 order-lg-2">{{
                      formatCurrency(
                        cartItem.prices.row_total_including_tax.value
                      )
                    }}</div>
                  </div>
                </div>
                <a href="#" class="cart-item-remove d-inline-block px-1" @click="removeItem(cartItem.id)"><ClientOnly><i class="fa-solid fa-xmark fa-2xl"></i></ClientOnly></a>
              </div>
            </div>
          </div>
          <div class="cart-discount-total row mt-5 pt-md-5">
            <div class="col-12 col-md-4 cart-discount">
              <h3 class="border-bottom border-black mb-4 pb-4">{{ $t("coupon_discount") }}</h3>
              <div class="input-group mb-3">
                <input
                  type="text"
                  class="form-control"
                  name="discount-code"
                  :placeholder="this.$t('coupon_discount')"
                  v-model="couponCode"
                />
                <button class="btn btn-primary" type="button" @click="addCoupon">{{ $t("apply") }}</button>
              </div>
            </div>
            <ClientOnly>
              <div class="col-12 col-md-5 offset-md-3 cart-total pt-5 pt-md-0">
                <h3 class="border-bottom border-black mb-4 pb-4">{{ $t("totals") }}</h3>
                <table class="table table-borderless fw-bold">
                  <tr>
                    <td>{{ $t("subtotal_btw") }}:</td>
                    <td>{{ formatCurrency(cartTotalWithTax) }}</td>
                  </tr>
                  <tr v-if="defaultShipping != null">
                    <td>{{ $t("shipping_cost") }}:</td>
                    <td v-if="defaultShipping.amount">{{
                      formatCurrency(defaultShipping.amount.value)
                    }}</td>
                  </tr>
                  <tr
                    v-for="(discount, index) in cartTotalDiscounts"
                    :key="index"
                  >
                    <td>{{ discount.label }}:</td>
                    <td>- {{ formatCurrency(discount.amount.value) }}</td>
                  </tr>
                  <tr
                    v-for="(price, index) in cartTotalTaxPrices"
                    :key="index"
                  >
                    <td>{{ price.label }}:</td>
                    <td>{{
                      formatCurrency(price.amount.value)
                    }}</td>
                  </tr>
                  <tr class="total-row">
                    <td>{{ $t("total_btw") }}:</td>
                    <td class="cart-total">{{
                      formatCurrency(cartTotalPrice)
                    }}</td>
                  </tr>
                </table>
              </div>
            </ClientOnly>
          </div>
          <div class="d-flex justify-content-end mt-5">
            <button class="btn btn-primary text-uppercase ms-4 px-5 cartplace-order" type="button" @click="$router.push('/checkout')">{{ $t("continue_buying") }}</button>
          </div>
        </form>
        <div class="offers my-5 pt-5" v-if="inActionProducts && inActionProducts.length > 0">
          <h2 class="section-heading"><span>{{ $t("offers") }}</span></h2>
          <ClientOnly>
            <div class="product-grid">
              <div class="swiper">
                <div class="grid-row row row-cols-2 row-cols-lg-3 row-cols-xl-4 swiper-wrapper">
                  <div class="col swiper-slide" v-for="(product,index) of inActionProducts" :key="`prod`+index">
                    <ProductCardWithPagination :product="product" v-if="product!=null"/>
                  </div>
                </div>
              </div>
              <div class="swiper-button-prev swiper-nav-round offers-swiper-button-prev"></div>
              <div class="swiper-button-next swiper-nav-round offers-swiper-button-next"></div>
              <div class="d-flex justify-content-between align-items-center my-5 d-lg-none">
                <p class="fs-6 mb-0">{{ $t("articles") }} {{ paginationConf.currentPageNr }} {{ $t("to") }} {{ Math.ceil(rows / paginationConf.perPage) }} {{ $t("by") }} {{ rows }} in {{ $t("total") }}</p>
                <b-pagination
                  v-model="paginationConf.currentPageNr"
                  :total-rows="rows"
                  :per-page="paginationConf.perPage"
                  @change="tmp"
                  class="cst-pagination ms-4 mt-3"
                  aria-controls="cat-item"
                  align="right"
                  :hide-goto-end-buttons="true"
                >
                  <template #page="{ page }">
                    <b-link class="page-link">
                      {{ page }}
                    </b-link>
                  </template>
                </b-pagination>
              </div>
            </div>
          </ClientOnly>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Breadcrumbs from "@/esf_antarctica_lederhosenland/core/components/breadcrumb/Breadcrumbs.vue";
import ProductCardWithPagination from "@/esf_antarctica_lederhosenland/core/components/category-product/ProductCardWithPagination.vue";
/*import CompareButton from "@/esf_antarctica_lederhosenland/core/components/common/CompareButton.vue";*/
import ClientOnly from "vue-client-only";
import { isMobile } from "mobile-device-detect";
import Cart from "@storefront/core/modules/cart/mixins";
import { Logger } from "@storefront/core/lib/logger";
import { mapGetters } from "vuex";

import config from "@config";

export default {
  name: "CartPage",
  components: {
    Breadcrumbs,
    ProductCardWithPagination,
    ClientOnly,
    /*CompareButton,*/
  },
  mixins: [Cart],
  data() {
    return {
      noOfItems: 1,
      couponCode: "",
      prices: [
        {
          id: "price1",
          name: "Verzendkosten (Verzenden - Via PostNL of DHL)",
          cost: "€ 0,00",
          timeDelivery: "Levertijd: 2 tot 4 werkdagen.",
        },
      ],
      paginationConf: {
        currentPageNr: 1,
        totalCount: 0,
        perPage: 4,
      },
      showOrders: true,
    };
  },
  computed: {
    ...mapGetters({
      related: "product/getRelated",
      isLoggedIn: "user/getIsLoggedIn",
    }),
    freeShippingAmount() {
      return this.$store.getters["cart/getFreeShippingAmount"];
    },
    cartTotalWithOutTax: function () {
      if (this.$store.getters["cart/getCartIsLoaded"] == true) {
        const prices = this.$store.getters["cart/getCartPrices"];
        if (typeof prices != "undefined") {
          if (typeof prices.subtotal_excluding_tax != "undefined") {
            if (typeof prices.subtotal_excluding_tax.value != "undefined") {
              return prices.subtotal_excluding_tax.value;
            }
          }
        }
        return 0;
      } else {
        return 0;
      }
    },
    cartTotalWithTax: function () {
      if (this.$store.getters["cart/getCartIsLoaded"] == true) {
        const prices = this.$store.getters["cart/getCartPrices"];
        if (typeof prices != "undefined") {
          if (typeof prices.subtotal_including_tax != "undefined") {
            if (typeof prices.subtotal_including_tax.value != "undefined") {
              return prices.subtotal_including_tax.value;
            }
          }
        }
        return 0;
      } else {
        return 0;
      }
    },
    totalCartTax: function () {
      if (this.$store.getters["cart/getCartIsLoaded"] == true) {
        const prices = this.$store.getters["cart/getCartPrices"];
        let totalTax = { value: 0 };
        if (
          typeof prices != "undefined" &&
          typeof prices.applied_taxes == "object"
        ) {
          prices.applied_taxes.forEach(function (amount) {
            Logger.debug(
              "totalCartTax amount",
              "cartList",
              amount.amount.value
            )();
            totalTax.value += amount.amount.value;
          });
        }
        Logger.debug("totalCartTax", "cartList", [
          { amount: totalTax, label: `$t("VAT")` },
        ])();
        return [{ amount: totalTax, label: this.$t("VAT") }];
      }
      //return [{ amount: {'value':0}, label: this.$t("VAT") }];
      return this.cartTotalTaxPrices;
    },
    inActionProducts() {
      const items = this.$store.getters["home/getInActionProducts"];
      if (items.length === 0) {
        return [];
      }
      if(isMobile){
        return items.slice(
          (this.paginationConf.currentPageNr - 1) * this.paginationConf.perPage,
          this.paginationConf.currentPageNr * this.paginationConf.perPage
        );
      }else{
        return items;
      }
    },
    rows() {
      return this.$store.getters["home/getInActionProducts"].length;
    }
  },
  methods: {
    formatCurrency(value) {
      var formatter = new Intl.NumberFormat("nl-NL", {
        style: "currency",
        currency: "EUR",
      });
      return formatter.format(value);
    },
    emptyCart() {
      this.cartItems.forEach((el) => this.removeItem(el.id));
    },
    async addCoupon() {
      if (this.couponCode != "") {
        const retval = await this.$store.dispatch("cart/addCouponCode", {
          code: this.couponCode,
        });
        Logger.debug("addCoupon", "cart.vue", retval)();
        if (retval == true) {
          const msg = {
            type: "success",
            title: this.$t("coupon_code"),
            text: this.$t("coupon_code_ok", { code: this.couponCode }),
          };
          this.$store.dispatch("messages/sendMessage", { message: msg });
          this.couponCode = "";
        }
      }
    },
    tmp(id) {
      this.showOrders = false;
      setTimeout(() => {
        this.showOrders = true;
        this.paginationConf.currentPageNr = id;
      }, 0.001);
    }
  },
  metaInfo() {
    return {
      meta: [
        {
          name: "title",
          content: this.$t("shopping_basket"),
        },
        {
          name: "keywords",
          content: this.$t("shopping_basket"),
        },
        {
          name: "description",
          content: this.$t("shopping_basket"),
        },
      ],
      //test 3
      // if no subcomponents specify a metaInfo.title, this title will be used
      title: this.$t("shopping_basket"),
      // all titles will be injected into this template
      link: [{ rel: "canonical", href: config.app_url + this.$route.fullPath }],
    };
  },
  mounted() {
    const bcrumb = { current: this.$t("cart"), routes: [] };
    this.$store.commit("breadcrumbs/set", bcrumb);

    if (this.$gtm.enabled()) {
      if (this.$gtm.ga4Enabled) {
        let items = [];
        this.cartItems.forEach((element) => {
          let i = {
            item_id: element.product.sku,
            item_name: element.product.name,
            quantity: element.quantity,
            currency: "EUR",
            price: element.prices.price.value,
          };
          items.push(i);
        });
        //GA4 event
        window.dataLayer.push({
          event: "view_cart",
          ecommerce: {
            add: {
              currency: "EUR",
              value: this.cartTotalWithTax,
              items: items,
            },
          },
        });
      }
    }
  },
};
</script>

<style lang="scss" scoped>
</style>
